import { useCallback, useMemo, useState } from 'react';

import { useLanguage } from '../../contexts/language';

import styles from './style.module.css';

// Choosing the questionnaire to assign (C34): a search by name or topic over
// every instrument, each shown with its title, a one-sentence description and
// the languages it exists in. Once one is chosen it is shown alone, with
// Change to choose again. onChoose is given the instrument, or nothing when
// the choice is undone.
export function InstrumentPicker({ instruments, chosen, onChoose }) {
  const { text } = useLanguage();
  const [ search, setSearch ] = useState('');

  const onSearch = useCallback(function({ currentTarget }) {
    setSearch(currentTarget.value);
  }, []);

  const pick = useCallback(function({ currentTarget }) {
    const [ code, version ] = currentTarget.value.split('\n');

    onChoose(instruments.find(function(instrument) {
      return instrument.code === code && instrument.version === version;
    }));
  }, [ instruments, onChoose ]);

  const change = useCallback(function() {
    onChoose();
  }, [ onChoose ]);

  const languagesOf = useCallback(function(codes) {
    return codes.map(function(code) {
      return text.languageNames[ code ];
    }).join(', ');
  }, [ text ]);

  const options = useMemo(function() {
    const wanted = search.trim().toLowerCase();

    return instruments.filter(function({ name, title, description }) {
      return `${ name } ${ title } ${ description }`.toLowerCase().includes(wanted);
    }).map(function({ code, version, name, title, description, languages }) {
      return (
        <button key={ `${ code }-${ version }` } className={ styles.option } type='button' value={ `${ code }\n${ version }` } onClick={ pick }>
          <span className={ styles.title }>{ `${ name } · ${ title }` }</span>
          <span className={ styles.description }>{ description }</span>
          <span className={ styles.languages }>{ text.availableIn.replace('{languages}', languagesOf(languages)) }</span>
        </button>
      );
    });
  }, [ instruments, search, pick, text, languagesOf ]);

  if (chosen != null) {
    return (
      <div className={ styles.field }>
        <span className={ styles.label }>{ text.questionnaire }</span>

        <div className={ styles.chosen }>
          <span className={ styles.lines }>
            <span className={ styles.title }>{ `${ chosen.name } · ${ chosen.title }` }</span>
            <span className={ styles.description }>{ chosen.description }</span>
          </span>

          <button className={ styles.change } type='button' onClick={ change }>{ text.change }</button>
        </div>
      </div>
    );
  }

  let list = <p className={ styles.empty }>{ text.noQuestionnairesFound }</p>;

  if (options.length > 0) {
    list = <div id='instrument-options' className={ styles.options } role='group' aria-label={ text.questionnaires }>{ options }</div>;
  }

  return (
    <div className={ styles.field }>
      <label className={ styles.label } htmlFor='instrument-search'>{ text.questionnaire }</label>

      <input
        id='instrument-search'
        className={ styles.search }
        type='search'
        placeholder={ text.searchByNameOrTopic }
        value={ search }
        onChange={ onSearch }
        aria-controls='instrument-options'
      />

      { list }
    </div>
  );
}
